
/*
 * Action Figure Essentials – Cart Recommendations
 * Checks all basket products for complementary items.
 */

(() => {
  const SECTION = 'afe-cart-recommendation-results';
  const MAX_RESULTS = 2;
  const root = window.Shopify?.routes?.root || '/';

  let refreshTimer;
  let requestVersion = 0;
  let lastKey = '';
  let lastContainer = null;

  async function getCart() {
    const response = await fetch(root + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }

  async function getRecommendations(productId) {
    const url = new URL(
      root + 'recommendations/products',
      window.location.origin
    );

    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', 'complementary');
    url.searchParams.set('section_id', SECTION);

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Recommendations unavailable');
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    return Array.from(
      doc.querySelectorAll(
        '.afe-recommendation-results ' +
        '.afe-recommendation-item[data-product-id]'
      )
    );
  }

  async function refreshRecommendations() {
    const container = document.getElementById(
      'AFE-Cart-Recommendations'
    );

    if (!container) return;

    const version = ++requestVersion;
    const productList = container.querySelector(
      '.afe-cart-recommendations__products'
    );

    if (!productList) return;

    try {
      const cart = await getCart();

      if (version !== requestVersion ||
          !container.isConnected) return;

      const productIds = [
        ...new Set(
          cart.items
            .map(item => String(item.product_id))
            .filter(Boolean)
        )
      ];

      const key = productIds.join(',');

      if (key === lastKey && container === lastContainer && container.dataset.afeLoaded === key) {
        return;
      }

      container.hidden = true;
      productList.replaceChildren();

      if (!productIds.length) return;

      const excluded = new Set(productIds);
      const selected = [];

      for (const productId of productIds) {
        if (version !== requestVersion) return;

        try {
          const suggestions = await getRecommendations(
            productId
          );

          for (const suggestion of suggestions) {
            const id = suggestion.dataset.productId;

            if (!id || excluded.has(id)) continue;

            excluded.add(id);
            selected.push(suggestion);

            if (selected.length >= MAX_RESULTS) break;
          }
        } catch (error) {
          console.warn('AFE recommendation lookup:', error);
        }

        if (selected.length >= MAX_RESULTS) break;
      }

      if (version !== requestVersion ||
          !container.isConnected ||
          document.getElementById(
            'AFE-Cart-Recommendations'
          ) !== container) return;

      if (selected.length) {
        productList.replaceChildren(
          ...selected.map(item => item.cloneNode(true))
        );
        container.hidden = false;
      }

      // Mark completed only AFTER results have finished loading.
      container.dataset.afeLoaded = key;
      lastKey = key;
      lastContainer = container;
    } catch (error) {
      console.warn('AFE cart recommendations:', error);
      lastKey = '';
      lastContainer = null;
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(
      refreshRecommendations,
      100
    );
  }


  function rotateEmptyCart() {
    const grid = document.querySelector('cart-drawer .afe-empty-cart__images');
    if (!grid) return;
    const items = Array.from(grid.querySelectorAll('.afe-empty-cart__candidate'));
    if (items.length < 3) return;
    // Uniformly choose two distinct products on each cart opening.
    for (const item of items) item.classList.remove('afe-empty-cart__chosen');
    const pool = items.slice();
    for (let i = 0; i < 2; i++) {
      const j = i + Math.floor(Math.random() * (pool.length - i));
      [pool[i], pool[j]] = [pool[j], pool[i]];
      pool[i].classList.add('afe-empty-cart__chosen');
    }
    grid.classList.add('afe-empty-cart__randomized');
  }

  function initialise() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    scheduleRefresh();
    rotateEmptyCart();
    // The drawer can be opened repeatedly without a page reload.
    const openObserver = new MutationObserver(() => {
      if (drawer.classList.contains('active')) rotateEmptyCart();
    });
    openObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    const observer = new MutationObserver(mutations => {
      const changed = mutations.some(mutation => {
        const target = mutation.target;

        if (
          target.nodeType === Node.ELEMENT_NODE &&
          target.closest('#AFE-Cart-Recommendations')
        ) {
          return false;
        }

        return mutation.type === 'childList';
      });

      if (changed) scheduleRefresh();
    });

    observer.observe(drawer, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialise,
      { once: true }
    );
  } else {
    initialise();
  }
})();
